import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, MonitorSmartphone } from 'lucide-react'
import apiClient from '@/api/client'
import { tagFromTerminalName } from '@/lib/offline/ticketToken'
import { desktop } from '@/lib/printTransport'
import {
  getPrintAgentConfig,
  setPrintAgentConfig,
  type PrintAgentConfig,
} from '@/lib/stationPrinterMap'
import { Switch } from '@/components/ui/switch'

/**
 * Designates THIS machine as the one that prints KOTs relayed from tablets
 * (F-FE-N60).
 *
 * Waiters on tablets cannot reach a printer that hangs off this PC by USB, so
 * their counter-bound slips are queued on the server and printed here. Nothing
 * is claimed unless this switch is on.
 *
 * Off by default, deliberately: if two counter PCs both claimed, a service's
 * tickets would split between two printers with no error anywhere — worse than
 * nothing printing, which is at least obvious.
 *
 * Desktop-only. A browser has no printer to lend.
 */
export function CounterPrintAgentSetting() {
  const isDesktop = !!desktop()
  const [cfg, setCfg] = useState<PrintAgentConfig>(() => getPrintAgentConfig())

  useEffect(() => {
    if (isDesktop) setCfg(getPrintAgentConfig())
  }, [isDesktop])

  // Only meaningful once the agent is on: how much paper is waiting, and
  // whether anything failed. Lets an operator confirm the relay is alive
  // without firing a test order from a tablet.
  const { data: auditRes } = useQuery({
    queryKey: ['print-jobs', 'audit'],
    queryFn: () => apiClient.getPrintJobsAudit({ limit: 200 }),
    enabled: isDesktop && cfg.enabled,
    refetchInterval: 15000,
  })

  if (!isDesktop) return null

  const jobs = auditRes?.data?.jobs ?? []
  const waiting = jobs.filter((j) => j.status === 'pending' || j.status === 'claimed').length
  const failed = jobs.filter((j) => j.status === 'failed').length
  const printed = jobs.filter((j) => j.status === 'printed').length
  // F-BE-N79: not printed within 10 minutes (no station polling), so never printed.
  const expired = jobs.filter((j) => j.status === 'expired').length

  const apply = (patch: Partial<PrintAgentConfig>) => setCfg(setPrintAgentConfig(patch))

  return (
    <div className="rounded-lg border border-border p-4">
      <div className="flex items-start justify-between gap-3">
        <label className="flex items-center gap-2 text-sm font-medium text-foreground">
          <MonitorSmartphone className="h-4 w-4" /> Print tablet KOTs on this PC
        </label>
        <Switch
          checked={cfg.enabled}
          onCheckedChange={(enabled) => apply({ enabled })}
          aria-label="Print tablet KOTs on this PC"
        />
      </div>

      <p className="mt-2 text-xs text-muted-foreground">
        Waiters on tablets can&apos;t reach a printer plugged into this computer by USB. Turn this on
        and their kitchen tickets for <strong>Print at counter</strong> stations will print here
        instead. Set which stations those are under <strong>Admin → Stations</strong>; each slip
        follows this machine&apos;s own printer mapping.
      </p>
      <p className="mt-2 text-xs text-muted-foreground">
        Turn this on for <strong>one</strong> counter PC only. If two claim the work, a service&apos;s
        tickets split between two printers.
      </p>

      {/*
        F-CTRL-N61: deliberately OUTSIDE the cfg.enabled block. This name is the prefix
        on every offline ticket this till prints, and the guidance directly above
        is to turn the print agent on for ONE PC only — so gating the field
        behind it left every other till unnameable, which is exactly where an
        unrecognisable tag costs the most.
      */}
      <div className="mt-3">
        <label className="mb-1 block text-xs font-medium text-foreground">Terminal name</label>
        <input
          type="text"
          value={cfg.terminalName}
          onChange={(e) => apply({ terminalName: e.target.value })}
          placeholder="Counter 1"
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Names this till on tickets it prints, and on kitchen slips it fires while offline —
          those read{' '}
          <span className="font-mono">
            OFF-{tagFromTerminalName(cfg.terminalName) || 'C1'}-001
          </span>
          .
        </p>
      </div>

      {cfg.enabled && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            {failed > 0 ? (
              <span className="flex items-center gap-1.5 font-medium text-destructive">
                <AlertTriangle className="h-3.5 w-3.5" />
                {failed} ticket{failed === 1 ? '' : 's'} failed to print
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                Relay active
              </span>
            )}
            <span className="text-muted-foreground">{printed} printed today</span>
            {expired > 0 && (
              <span
                className="font-medium text-destructive"
                title="Not printed within 10 minutes because no print station was polling. Use Reprint KOT if the order still needs it."
              >
                {expired} expired (station offline)
              </span>
            )}
            {waiting > 0 && (
              <span className="font-medium text-amber-600 dark:text-amber-400">
                {waiting} waiting
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
